import * as React from 'react';
import { useCallback, useContext, useMemo, useState } from 'react';

import { useQuery } from '@apollo/client';

import type { Assignment, CourseBranch, CourseItem, PeerReview, Product } from '@coursera/event-pulse-types';

import CenteredLoadingSpinner from 'bundles/assess-common/components/CenteredLoadingSpinner';
import { allowedFileTypesForAiGrading } from 'bundles/grading-common/utils/constants';
import type { Item } from 'bundles/learner-progress/types/Item';
import type {
  GetAiGradingEligibilityQuery,
  GetAiGradingEligibilityQueryVariables,
} from 'bundles/peer/api/__generated__/getAiGradingEligibility';
import type {
  GetFullyAiGradedConfigsByCourseQuery,
  GetFullyAiGradedConfigsByCourseQueryVariables,
} from 'bundles/peer/api/__generated__/getFullyAiGradedConfig';
import getAiGradingEligibilityQuery from 'bundles/peer/api/getAiGradingEligibility.graphql';
import getFullyAiGradedConfigQuery from 'bundles/peer/api/getFullyAiGradedConfig.graphql';
import type { Submission } from 'bundles/peer/types/Submission';
import {
  isFullyAiGradingEnabled,
  isPeerReviewAIGradingRolloutAllEnabledImpression,
  isPeerReviewAIGradingRolloutAllEnabledPreview,
  isPeerReviewAIGradingRolloutPaidEnabledImpression,
  isPeerReviewAIGradingRolloutPaidEnabledPreview,
  isPeerReviewFileAIGradingEnabledImpression,
  isPeerReviewFileAIGradingEnabledPreview,
} from 'bundles/peer/utils/epicUtils';

type PeerReviewEventingV3Data = {
  peerReview: PeerReview;
  assignment: Assignment;
  product: Product;
  courseItem: CourseItem;
  courseBranch: CourseBranch;
};

export type AiGradingContextType = {
  /**
   * Returns true if Assignment is eligible for any of AI Grading flows and user
   * is in a relevant treatment group. Checks if one of the conditions is true,
   * without making experiment impressions:
   * 1. Assignment is eligible for Fully AI Grading and User is in treatment
   * group for Fully AI Graded flow.
   * 2. Assignment is eligible for File AI Grading and User is in
   * treatment group for File AI Grading.
   * 3. Assignment is eligible for AI Grading and User is in one of the
   * treatment groups: Enterprise / Paid Consumer / Skillup.
   * 4. Assignment is eligible for AI Grading Full Rollout and User is in either
   * Enterprise or Paid Consumer.
   */
  isAiGradingFlowPreview: () => boolean;

  /**
   * Handles "mature" assignments eligible for AI-Grading.
   * Targets both enterprise and paid consumers full rollout, skillup,
   * courserians.
   */
  isAssignmentAiGradingRolloutPaidEligible: boolean;
  isAiEpicEnabledRolloutPaidImpression: () => boolean;

  /**
   * is in a select set of assignments for all learners full rollout
   */
  isAssignmentAiGradingRolloutAllEligible: boolean;
  isAiEpicEnabledRolloutAllImpression: () => boolean;

  /**
   * Returns true if Assignment is eligible for Fully AI Grading and User is in
   * treatment group for Fully AI Graded flow (no epic impression).
   */
  isAssignmentFullyAiGraded: boolean;

  /**
   * rollout experiment "file ai grading"
   */
  isAssignmentFileAiGradingEligible: boolean;
  isFileAiEpicEnabledPreview: boolean;
  isFileAiEpicEnabledImpression: () => boolean;

  peerReviewEventingV3Data: PeerReviewEventingV3Data;

  updateUnsupportedFileForAiGradingInSubmission: (fileUrl: string, unsupported: boolean) => void;
  doesSubmissionIncludeUnsupportedFileTypeForAiGrading: (submission: Submission) => boolean;
};

const AiGradingContext = React.createContext<AiGradingContextType | undefined>(undefined);

export function useAiGradingContext() {
  const context = useContext(AiGradingContext);
  if (context === undefined) {
    throw new Error(
      '`useAiGradingContext` was used outside of `AiGradingProvider`. Ensure your component is nested under `<AiGradingProvider>`.'
    );
  }
  return context;
}

export type AiGradingProviderProps = {
  computedItem: Item;
  assignmentId: string;
  assignmentVersion: number;
  courseBranchId: string;
  children: React.ReactNode;
};

const AiGradingProvider = ({
  computedItem,
  assignmentId,
  assignmentVersion,
  courseBranchId,
  children,
}: AiGradingProviderProps) => {
  const fullyAiGradingEnabled = isFullyAiGradingEnabled();

  const [loading, setLoading] = useState(true);
  const [fullyAiGradingLoading, setFullyAiGradingLoading] = useState(fullyAiGradingEnabled);
  const [unsupportedFilesForAiGradingInSubmission, setUnsupportedFilesForAiGradingInSubmission] = useState<
    Record<string, boolean>
  >({});

  const eligibilityResult = useQuery<GetAiGradingEligibilityQuery, GetAiGradingEligibilityQueryVariables>(
    getAiGradingEligibilityQuery,
    {
      variables: {
        assignmentId,
        assignmentVersion,
        courseId: computedItem.courseId,
        itemId: computedItem.id,
      },
      context: { clientName: 'gatewayGql' },
      onCompleted: () => setLoading(false),
      onError: () => setLoading(false),
    }
  );

  const fullyAiGradingResult = useQuery<
    GetFullyAiGradedConfigsByCourseQuery,
    GetFullyAiGradedConfigsByCourseQueryVariables
  >(getFullyAiGradedConfigQuery, {
    variables: {
      courseId: computedItem.courseId,
    },
    context: { clientName: 'gatewayGql' },
    skip: !fullyAiGradingEnabled,
    onCompleted: () => setFullyAiGradingLoading(false),
    onError: () => setFullyAiGradingLoading(false),
  });

  const isAssignmentFullyAiGraded = useMemo(() => {
    if (!fullyAiGradingEnabled || fullyAiGradingResult.loading) {
      return false;
    }

    return (
      fullyAiGradingResult.data?.PeerReviewAi?.getFullyAiGradedConfigsByCourse.find(
        (item) => item.assignmentId === assignmentId
      ) !== undefined
    );
  }, [fullyAiGradingResult, assignmentId, fullyAiGradingEnabled]);

  const isAssignmentAiGradingRolloutPaidEligible =
    !!eligibilityResult.data?.PeerReviewAi?.getAiGradingEligibility?.isEligible &&
    eligibilityResult.data?.PeerReviewAi?.getAiGradingEligibility?.features.includes('PAID_ENTERPRISE_100');

  const isAssignmentAiGradingRolloutAllEligible =
    !!eligibilityResult.data?.PeerReviewAi?.getAiGradingEligibility?.isEligible &&
    eligibilityResult.data?.PeerReviewAi?.getAiGradingEligibility?.features.includes('ROLLOUT_ALL');

  const isAssignmentFileAiGradingEligible =
    !!eligibilityResult.data?.PeerReviewAi?.getAiGradingEligibility?.isEligible &&
    eligibilityResult.data?.PeerReviewAi?.getAiGradingEligibility?.features.includes('FILE_UPLOAD');

  const isAiGradingFlowPreview = useCallback<() => $TSFixMe>(() => {
    return (
      isAssignmentFullyAiGraded ||
      (isAssignmentFileAiGradingEligible && isPeerReviewFileAIGradingEnabledPreview()) ||
      (isAssignmentAiGradingRolloutPaidEligible && isPeerReviewAIGradingRolloutPaidEnabledPreview()) ||
      (isAssignmentAiGradingRolloutAllEligible && isPeerReviewAIGradingRolloutAllEnabledPreview())
    );
  }, [
    isAssignmentFileAiGradingEligible,
    isAssignmentFullyAiGraded,
    isAssignmentAiGradingRolloutPaidEligible,
    isAssignmentAiGradingRolloutAllEligible,
  ]);

  if (loading || fullyAiGradingLoading) {
    return <CenteredLoadingSpinner />;
  }

  const peerReviewEventingV3Data: PeerReviewEventingV3Data = {
    peerReview: {
      isAiGradingEligible: isAiGradingFlowPreview(),
    },
    assignment: {
      id: assignmentId,
      version: assignmentVersion,
    },
    product: {
      id: computedItem.courseId,
      type: 'course',
    },
    courseItem: {
      id: computedItem.id,
      type: 'peer_review',
      name: computedItem.name,
    },
    courseBranch: {
      id: courseBranchId,
    },
  };

  const updateUnsupportedFileForAiGradingInSubmission = (fileUrl: string, unsupported: boolean) => {
    setUnsupportedFilesForAiGradingInSubmission((prevState) => ({
      ...prevState,
      [fileUrl]: unsupported,
    }));
  };

  const doesSubmissionIncludeUnsupportedFileTypeForAiGrading = (submission: Submission) => {
    return Object.values(submission.definition.parts).some((part) => {
      if (part.typeName !== 'fileUpload') {
        return false;
      }
      const fileUrl = part.definition.fileUrl;
      const fileExtension = fileUrl.split('.').pop()?.toLowerCase() || '';

      // check if the file is marked as unsupported in the state
      if (unsupportedFilesForAiGradingInSubmission[fileUrl]) {
        return true;
      }

      // we still want to check the submission as there might be previous draft state sessions
      // allow empty file extension for AI grading because some questions have them as optional
      return fileExtension !== '' && !Object.keys(allowedFileTypesForAiGrading).includes(fileExtension);
    });
  };

  return (
    <AiGradingContext.Provider
      value={{
        isAiGradingFlowPreview,

        // AI Grading full rollout to Paid & Enterprise, Skillup, Courserians
        isAiEpicEnabledRolloutPaidImpression: isPeerReviewAIGradingRolloutPaidEnabledImpression,
        isAssignmentAiGradingRolloutPaidEligible,

        // AI Grading full rollout to All
        isAiEpicEnabledRolloutAllImpression: isPeerReviewAIGradingRolloutAllEnabledImpression,
        isAssignmentAiGradingRolloutAllEligible,

        // AI Grading file upload
        isAssignmentFileAiGradingEligible,
        isFileAiEpicEnabledPreview: isPeerReviewFileAIGradingEnabledPreview(),
        isFileAiEpicEnabledImpression: isPeerReviewFileAIGradingEnabledImpression,

        // Fully AI Graded flow
        isAssignmentFullyAiGraded,

        peerReviewEventingV3Data,

        updateUnsupportedFileForAiGradingInSubmission,
        doesSubmissionIncludeUnsupportedFileTypeForAiGrading,
      }}
    >
      {children}
    </AiGradingContext.Provider>
  );
};

export default AiGradingProvider;
